import type { LineDraft } from "./LineItemsEditor";
import { vnDate } from "@hisense/shared";

/*
 * Bố cục trang Sửa hoá đơn chiếu lệ (19/09/2026, anh Toàn duyệt — cùng chuẩn với biểu mẫu sản phẩm).
 *
 * Số liệu production 19/09/2026 (850 dòng PI): mô tả, màu, cỡ, số lượng, đơn vị, đơn giá ~100%; MOQ 92,7%; mã phụ 59,5%;
 * giá EXW 21,4%; giá dưới MOQ 10,6%; mã mẫu khách hàng 3,9%; giá DTD 0,9%. Bốn cột cuối là CỘT PHỤ: ẩn khi cả PI không dòng
 * nào dùng, tự hiện khi có dòng dùng (không bao giờ giấu dữ liệu), bấm "Hiện cột phụ" để nhập.
 *
 * Bảng này là nguồn của bố cục; test bắt buộc mọi trường phần đầu và mọi trường của dòng hàng có đúng một chỗ.
 */

/** Phần đầu PI lúc tạo mới (chuyển từ trang sửa sang đây để test đếm được đủ trường). */
export const emptyPiHeader = () => ({
  billTo: "",
  shipTo: "",
  notifyParty: "",
  notifyPartyContact: "",
  consignee: "",
  consigneeContact: "",
  customerPoNo: "",
  customerDept: "",
  date: vnDate(),
  deliveryDate: "",
  shippingMode: "",
  portOfLoading: "",
  countryOfOrigin: "",
  dnNo: "",
  paymentTerms: "",
  currency: "USD",
  respBy: "",
  paymentCompanyId: "",
  shippingMarks: "",
  remarks: "",
  upcharge: "",
  discount: "",
});
export type PiHeader = ReturnType<typeof emptyPiHeader>;

export type PiGroupId = "parties" | "terms" | "shipping" | "lines" | "notes";

/**
 * Trường phần đầu theo nhóm. `shipTo` là ẢNH CHỤP người liên hệ của bên mua (tên cột cũ), nên nằm ở nhóm Các bên. Bộ phận
 * khách hàng sang nhóm Điều khoản cạnh PO khách hàng — cả hai là tham chiếu phía khách — để ma trận Các bên không hụt ô.
 */
export const PI_HEADER_GROUPS: readonly { id: PiGroupId; fields: readonly (keyof PiHeader)[] }[] = [
  { id: "parties", fields: ["billTo", "shipTo", "notifyParty", "notifyPartyContact", "consignee", "consigneeContact"] },
  { id: "terms", fields: ["date", "customerPoNo", "customerDept", "deliveryDate", "currency", "paymentCompanyId", "paymentTerms", "respBy"] },
  { id: "shipping", fields: ["shippingMode", "portOfLoading", "countryOfOrigin", "dnNo", "shippingMarks"] },
  { id: "lines", fields: ["upcharge", "discount"] },
  { id: "notes", fields: ["remarks"] },
];

/** Tuỳ chọn cả chứng từ (không nằm trong phần đầu `h`): đặt ở đầu trang, cạnh tiêu đề. */
export const PI_DOCUMENT_OPTIONS = ["printLanguage", "pProcess", "adminOnly"] as const;

/** Cột luôn hiện (LOT chỉ khi PI là P-Process). Cột mô tả sửa bản của ngôn ngữ in (tiếng Anh hoặc tiếng Hoa). */
export const PI_MAIN_COLUMNS = ["image", "productCode", "subRef", "lotNumber", "description", "color", "size", "quantity", "unit", "moq", "unitPrice"] as const;
/** Cột phụ — ít dùng (xem số liệu đầu tệp). */
export const PI_SIDE_COLUMNS = ["customerStyleNo", "exwPrice", "underMoqPrice", "dtdPrice"] as const;
/** Trường của dòng KHÔNG có ô: máy tự điền (chọn sản phẩm, đơn vị & giá) và phải gửi ngược nguyên vẹn khi lưu. */
export const PI_LINE_AUTOMATIC = ["id", "productId", "mcq", "msq", "unitSelection", "fxConversion", "fxApplied"] as const;

/** Trường dữ liệu của dòng mà mỗi cột mang. */
export const PI_COLUMN_FIELDS: Record<(typeof PI_MAIN_COLUMNS)[number] | (typeof PI_SIDE_COLUMNS)[number], readonly (keyof LineDraft)[]> = {
  // Ảnh sản phẩm (yêu cầu v1.8 mục 4.4): cột chỉ để XEM, dữ liệu đọc sống từ hồ sơ sản phẩm, không gửi lên khi lưu.
  image: ["imageUrl"], productCode: ["productCode"], subRef: ["subRef"], lotNumber: ["lotNumber"], description: ["description", "descriptionZh"],
  color: ["color"], size: ["size"], quantity: ["quantity"], unit: ["unit"], moq: ["moq"], unitPrice: ["unitPrice"],
  customerStyleNo: ["customerStyleNo"], exwPrice: ["exwPrice"], underMoqPrice: ["underMoqPrice"], dtdPrice: ["dtdPrice"],
};

/** Có dòng nào dùng cột phụ không — có thì cột phụ hiện và không ẩn được. */
export const piSideColumnsUsed = (lines: readonly LineDraft[]) =>
  lines.some((l) => PI_SIDE_COLUMNS.some((k) => String(l[k] ?? "").trim() !== ""));

/** Số ô vận chuyển đã có dữ liệu — mục Vận chuyển mở sẵn khi PI có dữ liệu ở đó. */
export const shippingFilledCount = (h: PiHeader) =>
  (["shippingMode", "portOfLoading", "countryOfOrigin", "dnNo", "shippingMarks"] as const).filter((k) => h[k].trim() !== "").length;
